import React, { useContext, useEffect, useState } from 'react'
import { AccountPageWrapper } from './AccountPageWrapper'
import { DSButton, DSDivider, DSFlex, DSIcon, DSIconName, DSSpacer, DSSurface, DSText } from '@zoe/ds-web'
import Link from 'next/link'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { useGetSubscriptions } from '@/lib/hooks/useGetSubscriptions'
import { ProductType, ZoeSubscriptionStatus } from 'clients/stripe-gateway'
import moment from 'moment'
import { trackAccountCardViewEvent, trackCtaClick } from '@/lib/gtm/gtmV2'
import { useRouter } from 'next/router'
import { useGetMemberDetails } from '@/lib/hooks/useGetMemberDetails'
import { GeoContext } from '@/context/geoContext'
import { isEmpty } from '@/lib/utils/helpers'

const NavigationTile = ({
  title,
  href,
  icon,
  testId,
  target = '_self',
  onClick,
}: {
  title: string
  href: string
  icon: DSIconName
  testId?: string
  target?: string
  onClick?: () => void
}) => {
  return (
    <Link href={href} data-testid={testId} target={target} onClick={onClick}>
      <DSSurface padding={16} radius={16} surfaceColor="teal-subtle">
        <DSIcon name={icon} size={24} />
        <DSSpacer size={48} direction="vertical" />
        <DSText variant="fluid-paragraph-300" weight="light">
          {title}
        </DSText>
      </DSSurface>
    </Link>
  )
}

const ProductCard = ({ title, imageUrl, imageAlt, content, onCTAClick, buttonText, testId }) => {
  return (
    <DSSurface surfaceColor="cream-subtle" padding={16} radius={16} testId={testId}>
      <DSFlex direction="row" gap={16} alignItems="flex-start">
        <img src={imageUrl} alt={imageAlt} width={90} style={{ objectFit: 'contain' }} />
        <div>
          <DSText variant="fluid-paragraph-400" weight="semiBold">
            {title}
          </DSText>
          <DSSpacer size={2} direction="vertical" />
          <DSText variant="fluid-paragraph-200">{content}</DSText>
          <DSSpacer size={8} direction="vertical" />
          <DSButton variant="primary" size="small" onPress={onCTAClick} testId={`${testId}-button`}>
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              {buttonText}
            </DSText>
          </DSButton>
        </div>
      </DSFlex>
    </DSSurface>
  )
}

const UpsellCard = ({ title, description, imageUrl, onClick, testId }) => {
  return (
    <DSSurface surfaceColor="cream-subtle" radius={16} testId={testId}>
      <div
        style={{
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'right top',
          backgroundRepeat: 'no-repeat',
          height: '202px',
          borderRadius: '16px',
        }}
      >
        <DSSpacer size={16} direction="vertical" />
        <DSFlex direction="row" alignItems="center">
          <DSSpacer size={16} direction="horizontal" />
          <DSFlex direction="column" justifyContent="space-between" width="40%" height="170px">
            <div>
              <DSText as="h2" variant="fluid-heading-500" weight="book">
                {title}
              </DSText>
              <DSSpacer size={16} direction="vertical" />
              <DSText variant="fluid-label-100" weight="light">
                {description}
              </DSText>
            </div>
            <DSButton variant="primary" size="small" onPress={onClick} testId={`${testId}-button`}>
              <DSText variant="fluid-paragraph-200" weight="semiBold">
                Try now
              </DSText>
            </DSButton>
          </DSFlex>
        </DSFlex>
      </div>
    </DSSurface>
  )
}

export const DashboardPage = () => {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const { country } = useContext(GeoContext)
  const { memberDetails: memberDetails, isLoading: memberDetailsLoading } = useGetMemberDetails()

  const {
    activeSubscriptions: membershipActiveSubscriptions,
    inactiveSubscriptions: membershipInActiveSubscriptions,
    loading: membershipSubscriptionLoading,
  } = useGetSubscriptions(ProductType.Program)

  const {
    activeSubscriptions: daily30ActiveSubscriptions,
    inactiveSubscriptions: daily30InActiveSubscriptions,
    pausedSubscriptions: daily30PausedSubscriptions,
    loading: daily30SubscriptionLoading,
  } = useGetSubscriptions(ProductType.Daily30)

  const [name, setName] = useState('')
  const [loading, setLoading] = useState(true)
  const [nextPaymentDate, setNextPaymentDate] = useState('')
  const [nextPaymentDateDaily30, setNextPaymentDateDaily30] = useState('')
  const [isMembershipAutoRenewalCancelled, setIsMembershipAutoRenewalCancelled] = useState(false)
  const [isDaily30AutoRenewalCancelled, setIsDaily30AutoRenewalCancelled] = useState(false)
  const [showMembershipProductCard, setShowMembershipProductCard] = useState(false)
  const [showDaily30Upsell, setShowDaily30Upsell] = useState(false)
  const [showDaily30ProductCard, setShowDaily30ProductCard] = useState(false)
  const [didMembershipExpire, setDidMembershipExpire] = useState(false)

  useEffect(() => {
    if (!memberDetailsLoading && !daily30SubscriptionLoading && !membershipSubscriptionLoading) {
      setName(memberDetails?.name?.first_name || '')
      setLoading(membershipSubscriptionLoading || daily30SubscriptionLoading)
      setNextPaymentDate(moment(membershipActiveSubscriptions[0]?.next_payment_date).format('DD MMM YYYY'))
      if (daily30ActiveSubscriptions[0]?.next_payment_date) {
        setNextPaymentDateDaily30(moment(daily30ActiveSubscriptions[0]?.next_payment_date).format('DD MMM YYYY'))
      }
      if (daily30PausedSubscriptions[0]?.next_payment_date) {
        setNextPaymentDateDaily30(moment(daily30PausedSubscriptions[0]?.next_payment_date).format('DD MMM YYYY'))
      }
      setIsMembershipAutoRenewalCancelled(membershipActiveSubscriptions[0] === undefined)
      setIsDaily30AutoRenewalCancelled(
        daily30ActiveSubscriptions[0] === undefined && daily30PausedSubscriptions[0] === undefined,
      )

      const haveBoughtDaily30 =
        daily30ActiveSubscriptions.length > 0 ||
        daily30InActiveSubscriptions.length > 0 ||
        daily30PausedSubscriptions.length > 0
      const haveBoughtMembership =
        membershipActiveSubscriptions.length > 0 || membershipInActiveSubscriptions.length > 0

      const inactiveMembershipSubscription = membershipInActiveSubscriptions[0] || null

      setDidMembershipExpire(
        inactiveMembershipSubscription && inactiveMembershipSubscription?.status === ZoeSubscriptionStatus.Ended,
      )
      setShowDaily30Upsell(!haveBoughtDaily30)

      setShowMembershipProductCard(haveBoughtMembership)
      setShowDaily30ProductCard(haveBoughtDaily30)

      if (memberDetails?.email_address) {
        setEmail(memberDetails?.email_address)
      }
    }
  }, [
    memberDetails,
    memberDetailsLoading,
    membershipActiveSubscriptions,
    membershipInActiveSubscriptions,
    daily30ActiveSubscriptions,
    daily30PausedSubscriptions,
    daily30InActiveSubscriptions,
    membershipSubscriptionLoading,
    daily30SubscriptionLoading,
  ])

  useEffect(() => {
    if (showMembershipProductCard) {
      trackAccountCardViewEvent({ productType: 'membership', cardType: 'subscription', email })
    }
  }, [showMembershipProductCard, email])

  useEffect(() => {
    if (showDaily30ProductCard) {
      trackAccountCardViewEvent({ productType: 'daily30', cardType: 'subscription', email })
    }
  }, [showDaily30ProductCard, email])

  useEffect(() => {
    if (showDaily30Upsell) {
      trackAccountCardViewEvent({ productType: 'daily30', cardType: 'upsell', email })
    }
  }, [showDaily30Upsell, email])

  return loading ? (
    <LoadingSpinner />
  ) : (
    <AccountPageWrapper loading={loading}>
      <DSSpacer size={16} direction="vertical" />
      <DSText variant="fluid-heading-600">Welcome back, {name}</DSText>
      <DSSpacer size={24} direction="vertical" />
      <DSSpacer size={16} direction="vertical" />

      {(showMembershipProductCard || showDaily30ProductCard) && (
        <DSText variant="fluid-paragraph-300" weight="regular">
          Your plan(s)
        </DSText>
      )}

      {!showMembershipProductCard && !showDaily30ProductCard && (
        <DSText variant="fluid-paragraph-400">You currently do not have any products to manage here.</DSText>
      )}

      {showMembershipProductCard && (
        <>
          <DSSpacer size={16} direction="vertical" />
          <ProductCard
            title="ZOE Membership"
            testId="membership-product-card"
            imageUrl="/assets/img/account/membership.png"
            imageAlt="Membership"
            content={
              isMembershipAutoRenewalCancelled ? (
                <>
                  Auto renewal cancelled.
                  {!didMembershipExpire && (
                    <>
                      <br /> Access until{' '}
                      {moment(membershipInActiveSubscriptions[0]?.cancellation_date).format('DD MMM YYYY')}
                    </>
                  )}
                </>
              ) : (
                `Next payment: ${nextPaymentDate}`
              )
            }
            onCTAClick={() => {
              router.push('/account/membership')
              trackCtaClick('account', 'dashboard-page-membership-manage')
            }}
            buttonText={isMembershipAutoRenewalCancelled ? 'Reactivate' : 'Manage'}
          />
        </>
      )}
      {showDaily30ProductCard && (
        <>
          <DSSpacer size={16} direction="vertical" />
          <ProductCard
            title="Daily 30+"
            testId="daily30-product-card"
            imageUrl="/assets/img/account/daily30.png"
            imageAlt="Daily 30+"
            content={
              isDaily30AutoRenewalCancelled ? 'Auto renewal cancelled.' : `Next payment: ${nextPaymentDateDaily30}`
            }
            onCTAClick={() => {
              router.push('/account/daily30')
              trackCtaClick('account', 'dashboard-page-daily30-manage')
            }}
            buttonText="Manage"
          />
        </>
      )}

      {(memberDetails?.shipping_address?.country_code === 'US' ||
        (isEmpty(memberDetails?.shipping_address?.country_code) && country === 'US')) && (
        <>
          <DSSpacer size={16} direction="vertical" />
          <DSText variant="fluid-paragraph-200">
            If you are a member of <span style={{ fontWeight: 'bold' }}>ZOE Plus</span>, you can manage or cancel your
            plan in your App/Play Store subscriptions.
          </DSText>
        </>
      )}

      <DSSpacer size={24} direction="vertical" />
      <DSDivider />
      <DSSpacer size={24} direction="vertical" />
      <DSFlex direction="column" width="100%" gap={16}>
        <DSFlex direction="row" width="100%" gap={16}>
          <DSFlex direction="column" justifyContent="space-between" width="50%">
            <NavigationTile
              title="Personal details"
              href="/account/profile"
              icon="person-info"
              testId="personal-details-navigation-tile"
              onClick={() => {
                trackCtaClick('account', 'dashboard-page-personal-details')
              }}
            />
          </DSFlex>
          <DSFlex direction="column" justifyContent="space-between" width="50%">
            <NavigationTile
              title="Payment details"
              href="/account/payment-details"
              icon="payment-history"
              testId="payment-details-navigation-tile"
              onClick={() => {
                trackCtaClick('account', 'dashboard-page-payment-details')
              }}
            />
          </DSFlex>
        </DSFlex>
        <DSFlex direction="row" width="100%" gap={16}>
          <DSFlex direction="column" justifyContent="space-between" width="50%">
            <NavigationTile
              title="Order history"
              href="/account/order-history"
              icon="box-info"
              testId="order-history-navigation-tile"
              onClick={() => {
                trackCtaClick('account', 'dashboard-page-order-history')
              }}
            />
          </DSFlex>
          <DSFlex direction="column" justifyContent="space-between" width="50%">
            <NavigationTile
              title="Help & Support"
              href="/faqs"
              icon="support"
              testId="help-support-navigation-tile"
              target="_blank"
              onClick={() => {
                trackCtaClick('account', 'dashboard-page-help-support')
              }}
            />
          </DSFlex>
        </DSFlex>
      </DSFlex>
      <DSSpacer size={24} direction="vertical" />
      {showDaily30Upsell && (
        <>
          <DSSpacer size={24} direction="vertical" />
          <DSText variant="fluid-paragraph-300" weight="regular">
            Why not try...
          </DSText>
          <DSSpacer size={16} direction="vertical" />

          {showDaily30Upsell && (
            <UpsellCard
              testId="daily30-upsell-card"
              title="Daily 30+"
              description="30 plants to add to your diet to maximize your scores."
              imageUrl="/assets/img/account/daily30-upsell.jpg"
              onClick={() => {
                trackCtaClick('account', 'dashboard-page-daily30-upsell')
                router.push('/daily30')
              }}
            />
          )}
          <DSSpacer size={24} direction="vertical" />
        </>
      )}
    </AccountPageWrapper>
  )
}
